import { $log } from './debug';

const objectToString = Object.prototype.toString;

export function yIsDate(v: any): v is Date {
    return objectToString.call(v) === '[object Date]';
}

export function yIsObject(v: any): boolean {
    return !!v && typeof v === 'object';
}

export function yIsRegExp(v: any): v is RegExp {
    return objectToString.call(v) === '[object RegExp]';
}

export function yIsPlainObject(v: any): boolean {
    const proto = Object.getPrototypeOf(v);
    return (!proto || proto === Object.prototype);
}

export function yIsEmpty(v: any): boolean {
    return v == null || v.length === 0 || !(v.length > 0 || Object.getOwnPropertyNames(v).length > 0);
}

export function yIsEmptyObject(v: any): boolean {
    return Object.keys(v).length === 0;
}

const yIsArray = Array.isArray;

///////////////////////////////
///// OBJECT MANIPULATION /////
///////////////////////////////
export function yMerge<T extends Object, U extends Object>(dst: T, src: U): T & U {
    const keys = Object.keys(src);
    for(let j = 0; j < keys.length; j++) {
        const key = keys[j];
        const val = (src as any)[key];

        if(!yIsObject(val)) {
            (dst as any)[key] = val;
        } else if(yIsDate(val)) {
            (dst as any)[key] = new Date(val);
        } else if(yIsRegExp(val)) {
            (dst as any)[key] = new RegExp(val);
        } else if(val.cloneNode) {
            (dst as any)[key] = val.cloneNode(true);
        } else {
            if(!yIsObject((dst as any)[key]))
                (dst as any)[key] = yIsArray(val) ? [] : {}
            yMerge((dst as any)[key], val);
        }
    }

    return dst as T & U;
}

export function yPick(src: { [prop: string]: any }, properties: string[]) {
    const dst: { [prop: string]: any } = {};
    for(const p of properties) {
        dst[p] = src[p];
    }
    return dst;
}

function _copyHelper(one: any): any {
    switch(objectToString.call(one)) {
        case '[object Int8Array]':
        case '[object Int16Array]':
        case '[object Int32Array]':
        case '[object Float32Array]':
        case '[object Float64Array]':
        case '[object Uint8Array]':
        case '[object Uint8ClampedArray]':
        case '[object Uint16Array]':
        case '[object Uint32Array]':
            return new one.constructor(one);

        case '[object ArrayBuffer]':
            return one.slice(0);

        case '[object Boolean]':
        case '[object Number]':
        case '[object String]':
        case '[object Date]':
            return new one.constructor(one.valueOf());

        case '[object RegExp]':
            const re = new RegExp(one.source, one.toString().match(/[^\/]*$/)[0]);
            re.lastIndex = one.lastIndex;
            return re;

        case '[object Array]':
            return undefined;

        case '[object Object]':
            if(yIsPlainObject(one))
                return undefined;

            //some other object, don't copy
            if(one.copyClone)
                return one.copyClone();
            else
                return one;

        default:
            return one;
    }
}

export function yShallowCopy<T>(src: T): T
export function yShallowCopy(src: any) {
    if(src === null || typeof src !== 'object')
        return src;

    let obj = _copyHelper(src);
    if(obj !== undefined)
        return obj;

    if(yIsArray(src))
        return src.slice(0);

    obj = {};
    for(const i in src) {
        obj[i] = src[i];
    }

    return obj;
}

export function yCopy<T>(src: T): T {
    const map = new WeakMap()

    function copyOne(one: any): any {
        if(one === null || typeof one !== 'object')
            return one;

        const already = map.get(one);
        if(already)
            return already;

        let obj = _copyHelper(one);
        if(obj !== undefined) {
            map.set(one, obj);
            return obj;
        }

        if(yIsArray(one)) {
            obj = [];
            map.set(one, obj);
            for(const o of one) {
                obj.push(copyOne(o));
            }

            return obj;
        } else {
            obj = {};
            map.set(one, obj);
            for(const i in one) {
                obj[i] = copyOne(one[i]);
            }

            return obj;
        }
    }

    return copyOne(src);
}

export function yDeepForEach(src: any, fn: (o: any) => void) {
    const set = new WeakSet()

    function deepForEachOne(one: any) {
        if(one == undefined) return;

        if(typeof one !== 'object') {
            fn(one);
            return;
        }

        if(set.has(one))
            return;
        set.add(one);
        fn(one);

        if(yIsArray(one)) {
            for(const o of one) {
                deepForEachOne(o);
            }
        } else {
            for(const i in one) {
                deepForEachOne(one[i]);
            }
        }
    }

    deepForEachOne(src);
}

export function yDeepSome(src: any, fn: (o: any) => boolean) {
    const set = new WeakSet()

    function deepSomeOne(one: any) {
        if(one == undefined) return false;

        if(typeof one !== 'object') {
            return fn(one);
        }

        if(set.has(one))
            return false;
        set.add(one);
        if(fn(one))
            return true;

        if(yIsArray(one)) {
            for(const o of one) {
                if(deepSomeOne(o))
                    return true;
            }
        } else {
            for(const i in one) {
                if(deepSomeOne(one[i]))
                    return true;
            }
        }

        return false;
    }

    return deepSomeOne(src);
}

export function yEqual<T>(lhs: T, rhs: T): boolean {
    let map: Map<any, Set<any>>;

    function equalOne(a: any, b: any): boolean {
        if(a === b) return true;
        if(a == undefined || b == undefined) return false;
        if(a !== a && b !== b) return true;
        if(typeof a !== 'object' || typeof b !== 'object') return false;

        map = map || new Map();
        let a_eq = map.get(a);
        if(a_eq) {
            if(a_eq.has(b)) return true;
            a_eq.add(b);
        } else {
            a_eq = new Set();
            a_eq.add(b);
            map.set(a, a_eq);
        }

        const a_type = objectToString.call(a);
        const b_type = objectToString.call(b);

        if(a_type !== b_type) return false;

        switch(a_type) {
            case '[object Date]':
                return a.getTime() === b.getTime();
            case '[object Array]':
                if(a.length !== b.length) return false;
                for(let i = 0; i < a.length; i++) {
                    if(!equalOne(a[i], b[i]))
                        return false;
                }
                return true;
            case '[object RegExp]':
                return a.toString() === b.toString();
            case '[object Object]':
                if(!yIsPlainObject(a) || !yIsPlainObject(b))
                    return false;
                const keys: any = {};
                for(let k in a) {
                    if(!equalOne(a[k], b[k]))
                        return false;
                    keys[k] = true;
                }
                for(let k in b) {
                    if(!keys[k] && b[k] !== undefined)
                        return false;
                }
                return true;
            default:
                return false;
        }
    }

    return equalOne(lhs, rhs);
}

export function yDictionary<T>(values: T[], key: (object: T) => string, dict: { [key: string]: T } = {}) {
    for(const v of values) {
        dict[key(v)] = v;
    }
    return dict;
}

export function yLogDiff<T>(lhs: T, rhs: T) {
    let map: Map<any, Set<any>>;
    let diff = false;

    function equalOne(path: string, a: any, b: any): boolean {
        if(a === b) return true;
        if(a == undefined || b == undefined) return false;
        if(a !== a && b !== b) return true;
        if(typeof a !== 'object' || typeof b !== 'object') return false;

        map = map || new Map();
        let a_eq = map.get(a);
        if(a_eq) {
            if(a_eq.has(b)) return true;
            a_eq.add(b);
        } else {
            a_eq = new Set();
            a_eq.add(b);
            map.set(a, a_eq);
        }

        const a_type = objectToString.call(a);
        const b_type = objectToString.call(b);

        if(a_type !== b_type) return false;

        switch(a_type) {
            case '[object Date]':
                return a.getTime() === b.getTime();
            case '[object Array]':
                if(a.length !== b.length) return false;
                for(let i = 0; i < a.length; i++) {
                    diffOne(path + '[' + i + ']', a[i], b[i]);
                }
                return true;
            case '[object RegExp]':
                return a.toString() === b.toString();
            case '[object Object]':
                if(!yIsPlainObject(a) || !yIsPlainObject(b))
                    return false;
                const keys: any = {};
                for(let k in a) {
                    diffOne(path + '.' + k, a[k], b[k]);
                    keys[k] = true;
                }
                for(let k in b) {
                    if(!keys[k] && b[k] !== undefined) {
                        $log('forced', path + '.' + k, a[k], b[k]);
                        diff = true;
                    }
                }
                return true;
            default:
                return false;
        }
    }

    function diffOne(path: string, a: any, b: any) {
        if(!equalOne(path, a, b)) {
            $log('forced', path, a, b);
            diff = true;
        }
    }

    diffOne('Object', lhs, rhs);
    if(!diff)
        $log('forced', 'Objects are equal');
}
